import { useEffect, useMemo, useState } from "react";
import type { AiProvider, AiProviderOption } from "@greg-chick/shared";
import {
  getNpcLines,
  saveNpcLines,
  draftNpcLines,
  aiProviders,
  type NpcLineDoc,
  type NpcLineKind,
} from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { Msg, Empty } from "./ui";

/**
 * NPC 대사 편집·검수(FR-WORLD/FR-NPC).
 *
 * 아이가 보는 건 **승인된 대사뿐**이다. 새로 쓴 대사·AI 초안은 대기함에 쌓이고,
 * 운영자가 ✅승인해야 마을에 나간다(아동안전 게이트).
 *
 * 대사는 서버가 방송하지 않는다 — 모든 브라우저가 같은 대사 풀과 같은 시각으로 같은 계산을
 * 해서 같은 장면을 본다. 그래서 실시간 서버(행사 때만 켬)가 없어도 365일 NPC 가 살아있다.
 */
const ZONES = [
  { id: "lobby", label: "마을(로비)" },
  { id: "gyeongju", label: "경주" },
  { id: "book", label: "전자책방" },
  { id: "goods", label: "굿즈 마켓" },
  { id: "event", label: "행사장" },
];
const KINDS: { id: NpcLineKind; label: string; hint: string }[] = [
  { id: "idle", label: "혼잣말", hint: "산책하다 툭 던지는 한 마디" },
  { id: "poi", label: "명소에서", hint: "첨성대 같은 장소에 도착했을 때" },
  { id: "pair", label: "둘이 대화", hint: "두 친구가 마주쳤을 때 (답까지 한 쌍)" },
  { id: "cheer", label: "칭찬·응원", hint: "아이가 별을 모으거나 해냈을 때" },
];
const NPCS = [
  { id: "", label: "아무나" },
  { id: "greg_white", label: "그레그" },
  { id: "greg_cat", label: "검은 고양이" },
  { id: "chick", label: "병아리" },
];

/** 명소 힌트 — 초안이 그 장소 이야기를 하게 만든다(존별 대표 지명). */
const ZONE_POIS: Record<string, string[]> = {
  lobby: ["마을 광장", "공지판", "느티나무"],
  gyeongju: ["첨성대", "불국사", "석굴암", "동궁과 월지", "대릉원"],
  book: ["그림책 서가", "읽어주는 자리"],
  goods: ["굿즈 진열대"],
  event: ["무대", "객석"],
};

/**
 * @param focusNpc 한 친구의 대사만 보여주는 모드([🧸 캐릭터] 화면이 끼워 쓴다).
 *                 그 친구 지정 대사 + "아무나" 대사를 보여준다 — "아무나"도 이 친구 입에서
 *                 나올 수 있으므로 숨기면 검수에서 빠진다(아이가 보는 말은 전부 사람이 본다).
 */
export function NpcLinePanel({ focusNpc }: { focusNpc?: string } = {}) {
  const [lines, setLines] = useState<NpcLineDoc[]>([]);
  const [zone, setZone] = useState("gyeongju");
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // AI 초안(선택) — 엣지에 키가 있는 공급자가 하나도 없으면 이 영역 자체를 그리지 않는다
  const [providers, setProviders] = useState<AiProviderOption[]>([]);
  const [provider, setProvider] = useState<AiProvider | "">("");
  const [aiKinds, setAiKinds] = useState<NpcLineKind[]>(["idle", "poi", "pair"]);
  const [aiCount, setAiCount] = useState(6);
  const [aiHint, setAiHint] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    getNpcLines()
      .then((l) => {
        setLines(l);
        setLoaded(true);
      })
      .catch(() => setMsg("엣지 서버에 연결하지 못했어요."));
    aiProviders()
      .then((ps) => {
        setProviders(ps);
        if (ps[0]) setProvider(ps[0].id); // 빈 값이면 select 가 아무것도 안 고른 상태로 보인다
      })
      .catch(() => setProviders([]));
  }, []);

  const inZone = useMemo(
    () =>
      lines.filter(
        (l) => l.zone === zone && (!focusNpc || !l.npc || l.npc === focusNpc),
      ),
    [lines, zone, focusNpc],
  );
  const pending = inZone.filter((l) => l.status === "pending");
  const approved = inZone.filter((l) => l.status === "approved");
  const rejected = inZone.filter((l) => l.status === "rejected");

  function patch(id: string, p: Partial<NpcLineDoc>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...p } : l)));
  }
  function addLine() {
    const id = `l_${Date.now().toString(36)}_${Math.floor(Math.random() * 1000)}`;
    setLines((prev) => [
      // 한 친구를 보고 있을 땐 그 친구 대사로 시작한다(고른 친구가 있는데 "아무나"로 만들면 헷갈린다)
      { id, zone, kind: "idle", text: "", status: "pending", source: "manual", ...(focusNpc ? { npc: focusNpc } : {}) },
      ...prev,
    ]);
  }
  function removeLine(id: string) {
    setLines((prev) => prev.filter((l) => l.id !== id));
  }

  /** AI 초안 — 받은 대사를 **대기함에 얹기만** 한다. 저장은 운영자가 눌러야 한다. */
  async function onDraft() {
    setAiBusy(true);
    setMsg("🤖 초안을 만드는 중…");
    try {
      const r = await draftNpcLines({
        zone,
        kinds: aiKinds,
        count: aiCount,
        pois: ZONE_POIS[zone] ?? [],
        hint: aiHint.trim() || undefined,
        provider: provider || undefined,
        existingIds: lines.map((l) => l.id),
      });
      setLines((prev) => [...r.lines, ...prev]);
      setMsg(
        `🤖 ${r.providerLabel} 초안 ${r.lines.length}개를 대기함에 넣었어요` +
          (r.dropped ? ` (안전하지 않은 ${r.dropped}개는 버렸어요)` : "") +
          `. 읽어보고 ✅승인한 것만 마을에 나가요. 오늘 ${r.remaining}번 더 만들 수 있어요.`,
      );
    } catch (e) {
      setMsg(`⚠️ 초안을 만들지 못했어요 — ${(e as Error).message}`);
    } finally {
      setAiBusy(false);
    }
  }

  async function onSave() {
    const clean = lines.filter((l) => l.text.trim());
    const r = await saveNpcLines(clean);
    setLines(clean);
    setMsg(`✅ 저장했어요 — 전체 ${r.total}개 중 승인 ${r.approved}개가 마을에 나가요.`);
  }
  // 저장 버튼은 화면 아래 고정 바가 그린다(어느 화면이든 같은 자리).
  useRegisterSave({
    id: "npc",
    label: "NPC 대사",
    save: onSave,
    draft: { data: () => lines, restore: (d) => setLines(d as NpcLineDoc[]) },
  });

  return (
    <section className="panel npcline">
      <div className="npcline__bar">
        <label>
          마을 고르기
          <select value={zone} onChange={(e) => setZone(e.target.value)}>
            {ZONES.map((z) => (
              <option key={z.id} value={z.id}>{z.label}</option>
            ))}
          </select>
        </label>
        <button className="btn" onClick={addLine} disabled={!loaded}>+ 직접 쓰기</button>
      </div>

      {focusNpc ? (
        <p className="hint">
          이 친구가 할 수 있는 말만 보여주고 있어요 — <b>이 친구 지정</b> 대사와{" "}
          <b>&ldquo;아무나&rdquo;</b> 대사예요. 저장은 전체 대사에 함께 반영돼요.
        </p>
      ) : null}

      {providers.length ? (
        <details className="npcline__ai-box">
          <summary>🤖 AI로 초안 만들기 (선택)</summary>
          <p className="hint">
            빈 화면 앞에서 막히지 않게 <b>밑그림</b>을 만들어 줘요. 만들어진 대사는 전부{" "}
            <b>대기함</b>으로 들어가고, 읽어보고 ✅승인한 것만 아이들에게 보여요.
          </p>
          <div className="npcline__ai-row">
            <label>
              어떤 AI
              <select value={provider} onChange={(e) => setProvider(e.target.value as AiProvider)}>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </label>
            <label>
              몇 개
              <input
                type="number"
                min={1}
                max={12}
                value={aiCount}
                onChange={(e) => setAiCount(Number(e.target.value))}
              />
            </label>
          </div>
          <div className="npcline__ai-kinds">
            {KINDS.map((k) => (
              <label key={k.id} className="npcline__chk">
                <input
                  type="checkbox"
                  checked={aiKinds.includes(k.id)}
                  onChange={(e) =>
                    setAiKinds((prev) =>
                      e.target.checked ? [...prev, k.id] : prev.filter((x) => x !== k.id),
                    )
                  }
                />
                {k.label}
              </label>
            ))}
          </div>
          <input
            className="npcline__text"
            value={aiHint}
            maxLength={200}
            placeholder="분위기 메모(선택) — 예: 가을 저녁, 별 보러 나온 느낌"
            onChange={(e) => setAiHint(e.target.value)}
          />
          <button className="btn btn--primary" onClick={() => void onDraft()} disabled={aiBusy || !aiKinds.length}>
            {aiBusy ? "만드는 중…" : "초안 만들기"}
          </button>
        </details>
      ) : null}

      <Msg text={msg} />

      <div className="npcline__counts">
        <span>⏳ 대기 <b>{pending.length}</b></span>
        <span>✅ 승인 <b>{approved.length}</b></span>
        <span>❌ 거절 <b>{rejected.length}</b></span>
      </div>

      {loaded && inZone.length === 0 ? (
        <Empty icon="💬" action="없어도 지도에 적어둔 기본 대사로 친구들이 이야기해요">
          이 마을엔 아직 대사가 없어요. <b>[+ 직접 쓰기]</b>로 한 줄 넣어보세요.
        </Empty>
      ) : null}

      {[
        { key: "pending", title: "⏳ 대기함 — 검수해 주세요", items: pending },
        { key: "approved", title: "✅ 마을에 나가는 대사", items: approved },
        { key: "rejected", title: "❌ 거절함", items: rejected },
      ].map((group) =>
        group.items.length ? (
          <div key={group.key} className="npcline__group">
            <h3>{group.title}</h3>
            <ul className="npcline__list">
              {group.items.map((l) => (
                <li key={l.id} className="npcline__item" data-status={l.status}>
                  <div className="npcline__row">
                    <select value={l.kind} onChange={(e) => patch(l.id, { kind: e.target.value as NpcLineKind })}>
                      {KINDS.map((k) => (
                        <option key={k.id} value={k.id} title={k.hint}>{k.label}</option>
                      ))}
                    </select>
                    <select value={l.npc ?? ""} onChange={(e) => patch(l.id, { npc: e.target.value || undefined })}>
                      {NPCS.map((n) => (
                        <option key={n.id} value={n.id}>{n.label}</option>
                      ))}
                    </select>
                    {l.source === "ai" ? <span className="npcline__ai">🤖 AI 초안</span> : null}
                  </div>

                  <input
                    className="npcline__text"
                    value={l.text}
                    maxLength={60}
                    placeholder="예: 오늘 달이 참 밝네요"
                    onChange={(e) => patch(l.id, { text: e.target.value })}
                  />

                  {l.kind === "pair" ? (
                    <input
                      className="npcline__text npcline__reply"
                      value={l.reply ?? ""}
                      maxLength={60}
                      placeholder="상대의 답 (예: 삐약! 정말요!)"
                      onChange={(e) => patch(l.id, { reply: e.target.value || undefined })}
                    />
                  ) : null}

                  {l.kind === "poi" ? (
                    <input
                      className="npcline__poi"
                      value={l.poi ?? ""}
                      maxLength={40}
                      placeholder="어느 명소? (비우면 모든 명소에서)"
                      onChange={(e) => patch(l.id, { poi: e.target.value || undefined })}
                    />
                  ) : null}

                  <div className="npcline__foot">
                    {l.status !== "approved" ? (
                      <button className="btn btn--sm btn--primary" onClick={() => patch(l.id, { status: "approved" })}>
                        ✅ 승인
                      </button>
                    ) : (
                      <button className="btn btn--sm" onClick={() => patch(l.id, { status: "pending" })}>
                        ⏳ 내리기
                      </button>
                    )}
                    {l.status !== "rejected" ? (
                      <button className="btn btn--sm" onClick={() => patch(l.id, { status: "rejected" })}>❌ 거절</button>
                    ) : null}
                    <button className="btn btn--sm" onClick={() => removeLine(l.id)}>삭제</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null,
      )}
    </section>
  );
}
